/**
 * The only reliable source for the language is in the HTTP-request header which could only be done serverside.
 * Instead we use navigator.languages, which properly returns the user's preferred languages as well.
 * Unfortunately IE and Safari don't support this yet - so we revert
 * to using navigator.language as a fallback - which is the browser's language.
 */
const getLanguage = () =>
  navigator.languages
    ? navigator.languages[0]
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigator.language || (navigator as any).userLanguage;

const toLocaleStringSupportsLocale = (number: number | string, locale: string) => {
  let localeNumberValue;

  try {
    localeNumberValue = number.toLocaleString([locale, 'en']);
  } catch (e) {
    localeNumberValue = number.toLocaleString();
  }

  return localeNumberValue;
};

export const localeNumber = (number?: number | string): string | undefined => {
  if (!number && number !== 0) {
    return undefined;
  }

  const language = getLanguage();
  let localeNumberValue: string = toLocaleStringSupportsLocale(number, language);

  /**
   * toLocaleString is not able to properly localize numbers passed as strings
   * which also include a decimal place. It is unsafe to rely on the Number() function as
   * that means the filter might alter the original number.
   *
   * To counter this, if the number has a decimal we reconstruct it manually.
   */
  if (typeof number === 'string' && number.indexOf('.') > 0) {
    const splitLocaleNumberValue = localeNumberValue.split('.');
    localeNumberValue =
      // eslint-disable-next-line prefer-template
      toLocaleStringSupportsLocale(Number(splitLocaleNumberValue[0]), language) +
      '.' +
      splitLocaleNumberValue[1];
  }

  return localeNumberValue;
};

export const abbrNumber = (number?: number): string | undefined => {
  if (!number && number !== 0) {
    return undefined;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  return formatter.format(number).toLowerCase();
};

/**
 * Return a number whose value is limited to the given range.
 *
 * Example: limit the output of this computation to between 0 and 255
 * (x * 255).clamp(0, 255)
 *
 * @param {Number} number The number whose value we want to limit
 * @param {Number} min The lower boundary of the output range
 * @param {Number} max The upper boundary of the output range
 * @returns Number in the range [min, max]
 */
export const clamp = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

/**
 * Rounds a number to the significant position.
 *
 * @param {Number} number
 * @param {Number} maxDecimalPlaces
 * @returns Number
 */
export function roundNumber(number: number, maxDecimalPlaces = 0): number {
  return +`${Math.round(+`${number}e+${maxDecimalPlaces}`)}e-${maxDecimalPlaces}`;
}

/**
 * Rounds a number with together with its ordinal.
 * e.g. 1 => '1st'
 * e.g. 12 => '12th'
 * e.g. 22 => '22nd'.
 *
 * @param {Number} number
 * @returns String
 */
export function numberWithOrdinal(number: number): string {
  const ordinal =
    number > 20 || number < 10 ? [false, 'st', 'nd', 'rd'][number % 10] || 'th' : 'th';

  return `${number}${ordinal}`;
}

/**
 * Round a number and always show one decimal point
 * e.g. 1.567 => '1.6'
 * e.g. 2 => '2' or '2.0' if showDigitEvenIfZero=true
 *
 * @param {Number} number
 * @param {Boolean} hideDigitIfZero
 * @returns String
 */
export function formatWithOneFractionDigit(number: number, showDigitEvenIfZero = false): string {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: showDigitEvenIfZero ? 1 : 0,
  }).format(number);
}

/**
 * Format a number and add the thousands separator, takes an optional number of decimals
 * @param num
 * @param decimalCount
 * @param minDecimalCount
 * @returns
 */
export function numberFormat(num: number, decimalCount = 2, minDecimalCount = 0): string {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimalCount,
    minimumFractionDigits: minDecimalCount,
  }).format(num);
}
